import { create } from 'zustand'
import { API_URL } from '../config'

const useAuthStore = create((set, get) => ({
  user: null,
  token: null,
  isLoading: false,
  error: null,
  isServerUnreachable: false,
  isSessionExpired: false,
  logoutMessage: null,

  setSessionExpired: (value) => {
    set({ isSessionExpired: value })
  },

  checkServerHealth: async () => {
    try {
      const response = await fetch(`${API_URL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Server health check failed')
      }
      
      set({ isServerUnreachable: false })
      return true
    } catch (error) {
      console.error('Server health check failed:', error)
      set({ isServerUnreachable: true })
      return false
    }
  },

  initializeAuth: async () => {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Initializing auth...')
      }
      
      const isHealthy = await get().checkServerHealth()
      if (!isHealthy) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('Server health check failed')
        }
        return false
      }

      const token = localStorage.getItem('token')
      if (process.env.NODE_ENV !== 'production') {
        console.log('Token from localStorage:', token ? 'exists' : 'not found')
      }

      if (!token) {
        set({ isSessionExpired: false })
        return true
      }

      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log('Verifying token...')
        }
        const response = await fetch(`${API_URL}/auth/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.ok) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Token verified successfully')
          }
          set({ token, isSessionExpired: false })
        } else {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Token verification failed')
            const error = await response.json()
            console.log('Error:', error)
          }
          localStorage.removeItem('token')
          set({ token: null, isSessionExpired: true })
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('Token verification error:', error)
        }
        localStorage.removeItem('token')
        set({ token: null, isSessionExpired: true })
      }
      
      return true
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Auth initialization error:', error)
      }
      set({ isServerUnreachable: true })
      return false
    }
  },

  isAuthenticated: () => {
    const token = get().token || localStorage.getItem('token')
    return !!token
  },

  clearError: () => set({ error: null, isSessionExpired: false }),

  login: async (email, password) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Starting login process...')
    }
    set({ isLoading: true, error: null, isSessionExpired: false })
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()
      if (process.env.NODE_ENV !== 'production') {
        console.log('Login response:', { status: response.status, data })
      }

      if (!response.ok) {
        throw new Error(data.message || 'Login failed')
      }

      localStorage.setItem('token', data.token)
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('Setting auth state after successful login')
      }
      
      set({
        user: data.user,
        token: data.token,
        isLoading: false,
        error: null,
        isSessionExpired: false
      })

      await new Promise(resolve => setTimeout(resolve, 100))
      
      return true
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Login error:', error)
      }
      set({
        isLoading: false,
        error: error.message || 'Failed to connect to server',
        isSessionExpired: false
      })
      return false
    }
  },

  register: async (email, password) => {
    set({ isLoading: true, error: null })
    try {
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed')
      }

      set({
        user: data.user,
        token: data.token,
        isLoading: false,
        error: null,
      })

      localStorage.setItem('token', data.token)
      return true
    } catch (error) {
      console.error('Registration error:', error)
      set({
        isLoading: false,
        error: error.message || 'Failed to connect to server',
      })
      return false
    }
  },

  logout: async () => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Logging out...')
    }
    
    try {
      const token = get().token
      if (token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      }
    } catch (error) {
      console.error('Logout request failed:', error)
    } finally {
      localStorage.removeItem('token')
      localStorage.removeItem('lastSelectedImageId')
      set({
        user: null,
        token: null,
        error: null,
        isSessionExpired: false,
        isLoading: false,
        isServerUnreachable: false,
        logoutMessage: 'You have been logged out.'
      })
    }
  },

  setToken: (token) => set({ token }),

  clearLogoutMessage: () => set({ logoutMessage: null }),
}))

export default useAuthStore 